/**
 * Ekster: Kaching Bundles enhancer.
 *
 * The PDP add-ons and the wallet bundle offer are managed in Kaching Bundles. Kaching has no
 * info dialog, no discount badge, no free gift badge and no gift note, so this script clones
 * the theme's own parts into the widget:
 *
 * 1. info dialog trigger (section-dialog-loader + fetch-product-add-on-dialog) per add-on row
 * 2. discount badge, derived from the prices Kaching renders
 * 3. theme toggle, or an Add button that adds the add-on straight to the cart
 * 4. green FREE GIFT badge next to any item in the bundle that Kaching prices at zero
 * 5. gift note with the gift icon under the selected bar, in the row Kaching keeps for its own
 *    free gift line, so the copy survives removing the gift popup from the Kaching app
 * 6. pack variant dropdowns hidden, colour dropdowns kept, so a multipack cannot be picked for
 *    the price of a single pack
 *
 * Product handle and url per row come from the kaching-bundle element itself
 * (other-products + deal-block attributes), so nothing is duplicated in the theme.
 *
 * Kaching renders with Vue and re-renders on variant and currency changes. A MutationObserver
 * per kaching-bundle re-applies the enhancement; every write is idempotent.
 */
(() => {
  const ROOT_SELECTOR = '[data-ek-kaching-add-ons]';
  const UPSELL_SELECTOR = '.kaching-bundles__checkbox-upsells__upsell';
  const BUNDLE_PRODUCT_SELECTOR = '.kaching-bundles__bundle-products__product';
  const ROW_MARK = 'data-ek-kaching-add-on';
  const HIDDEN_MARK = 'data-ek-kaching-hidden';

  const root = document.querySelector(ROOT_SELECTOR);
  if (!root) return;

  let config = {};
  try {
    config = JSON.parse(root.querySelector('[data-ek-kaching-add-ons-config]')?.textContent || '{}');
  } catch (error) {
    config = {};
  }

  const tooltipTemplate = root.querySelector('template[data-ek-kaching-tooltip]');
  const badgeTemplate = root.querySelector('template[data-ek-kaching-badge]');
  const switchTemplate = root.querySelector('template[data-ek-kaching-switch]');
  const freeBadgeTemplate = root.querySelector('template[data-ek-kaching-free-badge]');
  const noteTemplate = root.querySelector('template[data-ek-kaching-note]');
  const addTemplate = root.querySelector('template[data-ek-kaching-add]');

  const addMode = config.selector === 'add' && Boolean(addTemplate);
  const switchEnabled = !addMode && config.selector !== 'checkbox' && Boolean(switchTemplate);
  const badgeEnabled = Boolean(config.badge && badgeTemplate);
  const freeBadgeEnabled = Boolean(config.freeBadge && freeBadgeTemplate);
  const noteMode = noteTemplate ? config.note || 'off' : 'off';
  const noteOnUnselected = config.noteUnselected !== false;
  const variantMode = config.variantSelects || 'all';
  const observed = new WeakSet();

  /**
   * Parse a JSON attribute off the kaching-bundle element without throwing.
   */
  const readJson = (element, attribute) => {
    const raw = element.getAttribute(attribute);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  };

  /**
   * Map product id -> { handle, url, title } from the other-products attribute.
   * The attribute repeats products, a map by id dedupes it.
   */
  const readProducts = (bundle) => {
    const map = new Map();
    const list = readJson(bundle, 'other-products');
    if (!Array.isArray(list)) return map;
    list.forEach((product) => {
      if (product && product.id != null && !map.has(String(product.id))) {
        map.set(String(product.id), product);
      }
    });
    return map;
  };

  /**
   * Ordered product ids of the checkbox upsells from the deal-block attribute.
   */
  const readUpsellIds = (bundle) => {
    const block = readJson(bundle.querySelector('kaching-bundles-block') || bundle, 'deal-block');
    const upsells = block?.checkboxUpsells?.upsells;
    if (!Array.isArray(upsells)) return [];
    return upsells.map((upsell) => {
      const gid = String(upsell?.productGID || '');
      const match = gid.match(/\/(\d+)$/);
      return match ? match[1] : '';
    });
  };

  /**
   * Locale prefix of the storefront, taken from the main product url Kaching carries
   * ("/en-eu/products/ekster-wallet-pro" -> "/en-eu"), falling back to Shopify.routes.
   */
  const readPrefix = (bundle) => {
    const product = readJson(bundle, 'product');
    const url = product?.url || '';
    const match = url.match(/^(.*?)\/products\//);
    if (match) return match[1];
    const root = window.Shopify?.routes?.root || '/';
    return root.replace(/\/$/, '');
  };

  /**
   * Ordered product handles of the checkbox upsells (deal-block otherProductHandles).
   */
  const readUpsellHandles = (bundle) => {
    const block = readJson(bundle.querySelector('kaching-bundles-block') || bundle, 'deal-block');
    return Array.isArray(block?.otherProductHandles) ? block.otherProductHandles : [];
  };

  const handleize = (text) =>
    (text || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  /**
   * Resolve the product behind a rendered upsell row.
   * 1. other-products entry matched on the image alt (title), then by config order
   * 2. deal-block otherProductHandles by config order
   * 3. handleized image alt as a last resort
   */
  const resolveProduct = (row, index, bundle, products, upsellIds, upsellHandles) => {
    const alt = (row.querySelector('img')?.getAttribute('alt') || '').trim();
    let product = null;
    if (alt) {
      for (const candidate of products.values()) {
        if ((candidate.title || '').trim() === alt) {
          product = candidate;
          break;
        }
      }
    }
    if (!product && upsellIds[index]) product = products.get(upsellIds[index]) || null;
    const prefix = readPrefix(bundle);
    const handle = product?.handle || upsellHandles[index] || handleize(alt);
    return {
      id: product?.id != null ? String(product.id) : '',
      handle,
      url: product?.url || (handle ? `${prefix}/products/${handle}` : ''),
    };
  };

  const resolvePathname = (row, index, bundle, products, upsellIds, upsellHandles) =>
    resolveProduct(row, index, bundle, products, upsellIds, upsellHandles).url;

  /**
   * Parse a money string the way Kaching prints it (any symbol, either separator style).
   * Returns null when there is no number in it.
   */
  const parseMoney = (text) => {
    const clean = (text || '').replace(/[\s\u00a0]/g, '');
    const match = clean.match(/[\d.,']+/);
    if (!match) return null;
    let numeric = match[0].replace(/'/g, '');
    const prefix = clean.slice(0, match.index);
    const suffix = clean.slice(match.index + match[0].length);
    const separators = numeric.match(/[.,]/g) || [];
    let decimalSeparator = '';
    if (separators.length) {
      const last = numeric.lastIndexOf(separators[separators.length - 1]);
      const tail = numeric.length - last - 1;
      const allSame = separators.every((separator) => separator === separators[0]);
      // A single separator followed by 3 digits is a thousands separator (1.234), anything
      // else on the last position is the decimal separator (39,00 / 39.5 / 1.234,56).
      if (tail === 3 && allSame && separators.length === 1 && numeric.length > 4) {
        decimalSeparator = '';
      } else if (tail !== 3 || !allSame) {
        decimalSeparator = numeric[last];
      }
    }
    let value;
    if (decimalSeparator) {
      const last = numeric.lastIndexOf(decimalSeparator);
      value = parseFloat(
        numeric.slice(0, last).replace(/[.,]/g, '') + '.' + numeric.slice(last + 1)
      );
    } else {
      value = parseFloat(numeric.replace(/[.,]/g, ''));
    }
    if (Number.isNaN(value)) return null;
    return { value, prefix, suffix };
  };

  /**
   * A price is free when Kaching prints a zero amount, or a word in place of one
   * ("FREE", "Gratis"), which is what it does for a gift line.
   */
  const isFreePrice = (text) => {
    const raw = (text || '').trim();
    if (!raw) return false;
    const amount = parseMoney(raw);
    if (amount === null) return true;
    return amount.value === 0;
  };

  /**
   * Badge copy for a row from the rendered prices. Null means no badge.
   */
  const badgeContent = (row) => {
    const priceEl = row.querySelector('.kaching-bundles__bar-price');
    const fullEl = row.querySelector('.kaching-bundles__bar-full-price');
    if (!priceEl || !fullEl) return null;
    const price = parseMoney(priceEl.textContent);
    const full = parseMoney(fullEl.textContent);
    if (!price || !full || full.value <= 0 || full.value <= price.value) return null;
    const saved = full.value - price.value;
    const percentage = Math.round((saved / full.value) * 100);
    if (percentage <= 0 || percentage >= 100) return null;
    if (config.format === 'amount') {
      // Same shape as price-badges.liquid: whole units with the currency affix.
      const amount = `${full.prefix}${Math.round(saved)}${full.suffix}`;
      return (config.textAmount || 'Save [amount_saved]').replace('[amount_saved]', amount);
    }
    return (config.textPercentage || '[percentage]% off').replace('[percentage]', percentage);
  };

  /**
   * Keep the info dialog trigger from toggling the Kaching checkbox row it sits in.
   */
  const isolate = (button) => {
    ['click', 'keydown', 'mousedown', 'touchstart', 'pointerdown'].forEach((type) => {
      button.addEventListener(type, (event) => event.stopPropagation());
    });
  };

  const addTooltip = (row, pathname) => {
    if (!tooltipTemplate || !pathname) return;
    if (row.querySelector('.ek-kaching-tooltip')) return;
    // The first line div next to the title, not the title span: Vue may rewrite the span's
    // text on re-render, which would wipe anything nested inside it.
    const line = row.querySelector('.kaching-bundles__bar-first-line');
    if (!line) return;
    // Insert as a string so the custom element upgrades with its attributes already present.
    // Leading the line keeps the title and the discount badge next to each other.
    const html = tooltipTemplate.innerHTML.replace('[pathname]', pathname);
    line.insertAdjacentHTML('afterbegin', html);
    const button = line.querySelector('.ek-kaching-tooltip > button');
    if (button) isolate(button);
  };

  /**
   * Discount badge for a row. It sits on the first line next to the title, which is where the
   * selector bars carry their own Kaching label, so both parts of the widget read the same way.
   * The right hand column is then left with the price and the struck through full price only.
   * Falls back to the pricing column when Kaching renders a row without a first line.
   */
  const syncBadge = (row) => {
    if (!badgeEnabled) return;
    const host =
      row.querySelector('.kaching-bundles__bar-first-line') ||
      row.querySelector('.kaching-bundles__bar-pricing');
    if (!host) return;
    const content = badgeContent(row);
    // A previous render may have left the badge in the other host, so look up from the row.
    let badge = row.querySelector('.ek-kaching-badge');
    if (!content) {
      if (badge) badge.remove();
      return;
    }
    if (badge && badge.parentElement !== host) {
      badge.remove();
      badge = null;
    }
    if (!badge) {
      host.insertAdjacentHTML('beforeend', badgeTemplate.innerHTML);
      badge = host.querySelector('.ek-kaching-badge');
      if (!badge) return;
      badge.removeAttribute('id');
    }
    if (badge.textContent !== content) badge.textContent = content;
  };

  /**
   * Green FREE GIFT badge next to the title of any free item, in the bundle contents list and
   * on the add-on rows. Free is read off the price Kaching renders, so the item keeps its own
   * product title and nothing has to be renamed in the Kaching admin.
   */
  const syncFreeBadge = (host, priceEl, titleEl) => {
    if (!freeBadgeEnabled || !titleEl) return;
    const free = Boolean(priceEl) && isFreePrice(priceEl.textContent);
    let badge = host.querySelector('.ek-kaching-free-badge');
    if (!free) {
      if (badge) badge.remove();
      return;
    }
    if (badge) return;
    // After the title, never inside it: Vue rewrites the title node's text on re-render.
    titleEl.insertAdjacentHTML('afterend', freeBadgeTemplate.innerHTML);
    badge = host.querySelector('.ek-kaching-free-badge');
    if (badge) badge.removeAttribute('id');
  };

  const syncFreeBadges = (bundle) => {
    if (!freeBadgeEnabled) return;
    bundle.querySelectorAll(BUNDLE_PRODUCT_SELECTOR).forEach((product) => {
      syncFreeBadge(
        product,
        product.querySelector('.kaching-bundles__bundle-products__price'),
        product.querySelector('.kaching-bundles__bundle-products__title')
      );
    });
    bundle.querySelectorAll(UPSELL_SELECTOR).forEach((row) => {
      const line = row.querySelector('.kaching-bundles__bar-first-line');
      if (!line) return;
      syncFreeBadge(
        line,
        row.querySelector('.kaching-bundles__bar-price'),
        line.querySelector('.kaching-bundles__bar-title')
      );
    });
  };

  /**
   * Does this bar hand the shopper something for free? True when any item in the bundle
   * contents is priced at zero, or when Kaching still renders its own free gift line.
   */
  const barHasFreeItem = (bar) => {
    const free = [...bar.querySelectorAll(BUNDLE_PRODUCT_SELECTOR)].some((product) =>
      isFreePrice(product.querySelector('.kaching-bundles__bundle-products__price')?.textContent)
    );
    if (free) return true;
    return Boolean(bar.querySelector('.kaching-bundles__free-gift'));
  };

  /**
   * Gift note under the selected bar. It goes last inside the bar container, which is the slot
   * Kaching uses for its own free gift line, so the copy stays in place once that line and the
   * gift popup are switched off in the Kaching app.
   */
  /**
   * Free status per bar, cached so it survives the bar collapsing. A bar that was expanded once
   * keeps its answer; a bar that has never been open falls back to the free gift element, which
   * Kaching renders on the bar itself.
   */
  const freeItemCache = new WeakMap();
  const rememberFreeItem = (bar, expanded) => {
    if (expanded || bar.querySelector(BUNDLE_PRODUCT_SELECTOR)) {
      const value = barHasFreeItem(bar);
      freeItemCache.set(bar, value);
      return value;
    }
    if (freeItemCache.has(bar)) return freeItemCache.get(bar);
    return Boolean(bar.querySelector('.kaching-bundles__free-gift'));
  };

  const syncNote = (bundle) => {
    if (noteMode === 'off') return;
    bundle.querySelectorAll('.kaching-bundles__bar').forEach((bar) => {
      const container = bar.querySelector('.kaching-bundles__bar-container');
      if (!container) return;
      const selected = bar.classList.contains('kaching-bundles__bar--selected');
      // QA 03 Sep (Shezre): with noteOnUnselected the note stays on every qualifying offer, so a
      // shopper sitting on the single item keeps seeing what the bundle adds. Kaching collapses
      // the product list on an unselected bar, so barHasFreeItem cannot read prices there: the
      // free status is remembered per bar from the last time it was expanded, and until then the
      // bar's own free gift element is the fallback (it is present whether or not the bar is open).
      const qualifies = noteMode === 'always' || rememberFreeItem(bar, selected);
      const wanted = (selected || noteOnUnselected) && qualifies;
      const note = container.querySelector(':scope > .ek-kaching-note');
      if (!wanted) {
        if (note) note.remove();
        return;
      }
      // Insert once and leave it there. Moving it on every mutation would fight Vue's own
      // patching and could loop the observer.
      if (note) return;
      container.insertAdjacentHTML('beforeend', noteTemplate.innerHTML);
    });
  };

  /**
   * Pack values look like "1 Pack", "2 Pack", "Pack of 3", "Buy 2 Get 1 FREE", "3er Pack", "2x",
   * "Multipack". Colour values never do, which is the whole rule: pack dropdowns go, colour
   * dropdowns stay. The option NAME is checked first (Kaching prints it on the select as
   * data-name / aria-label and in the label span), against the theme's bundle option name
   * (settings "Bundle option name", "Pack" on Ekster) plus the usual pack and quantity names.
   */
  const PACK_PATTERN =
    /(^|\b)(\d+\s*(x|er)?\s*[-\u2013]?\s*(pack(s|ung)?|pk|pcs|pieces|stuks?|st(u|ü)ck)|pack of\s*\d+|multi[-\s]?pack|\d+\s*x|buy\s*\d+\s*(,|and|&)?\s*get\s*\d+)(\b|$)/i;
  const PACK_NAME_PATTERN = /^(pack|packs|packung|quantity|qty|bundle|amount|aantal|multipack|size of pack)$/i;
  const packOptionName = (config.packOptionName || '').trim().toLowerCase();

  const isPackName = (text) => {
    const raw = (text || '').replace(/[:\s]+$/, '').trim();
    if (!raw) return false;
    if (packOptionName && raw.toLowerCase() === packOptionName) return true;
    return PACK_NAME_PATTERN.test(raw);
  };

  const isPackLabel = (text) => {
    const raw = (text || '').trim();
    if (!raw) return false;
    return PACK_PATTERN.test(raw);
  };

  /**
   * Labels offered by one variant select. A native select exposes its options; the swatch style
   * dropdown only renders its options once opened, so the current label is what there is.
   */
  const selectLabels = (select) => {
    if (select.tagName === 'SELECT') {
      return [...select.options].map((option) => option.textContent || '');
    }
    const options = select.querySelectorAll(
      '.kaching-bundles__bar-variant-option__name-label, .kaching-bundles__swatch-dropdown__option, option'
    );
    if (options.length) return [...options].map((option) => option.textContent || '');
    return [select.textContent || ''];
  };

  /**
   * Lowest pack option of a native select, so hiding the dropdown always leaves the single pack
   * selected rather than whatever Kaching defaults to.
   */
  const lowestPackOption = (select) => {
    let best = null;
    let bestCount = Infinity;
    [...select.options].forEach((option) => {
      const match = (option.textContent || '').match(/\d+/);
      const count = match ? parseInt(match[0], 10) : 1;
      if (count < bestCount) {
        bestCount = count;
        best = option;
      }
    });
    return best;
  };

  const setHidden = (element, hidden) => {
    if (!element) return;
    if (hidden === element.hasAttribute(HIDDEN_MARK)) return;
    if (hidden) element.setAttribute(HIDDEN_MARK, '');
    else element.removeAttribute(HIDDEN_MARK);
  };

  /**
   * Name of the option a select belongs to: the select's own data-name / aria-label, else the
   * label span Kaching renders next to it ("Pack:").
   */
  const optionNameOf = (select) => {
    const own = select.getAttribute('data-name') || select.getAttribute('aria-label');
    if (own) return own;
    const option = select.closest('.kaching-bundles__bar-variant-option');
    return option?.querySelector('.kaching-bundles__bar-variant-option__name')?.textContent || '';
  };

  const isPackSelect = (select) =>
    isPackName(optionNameOf(select)) || selectLabels(select).every(isPackLabel);

  const syncVariantSelects = (bundle) => {
    if (variantMode === 'all') return;
    bundle.querySelectorAll('.kaching-bundles__bar-variant-select').forEach((select) => {
      const hide = variantMode === 'hide_all' || isPackSelect(select);
      if (hide && select.tagName === 'SELECT') {
        const option = lowestPackOption(select);
        if (option && select.value !== option.value) {
          select.value = option.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      setHidden(select, hide);
      // Take the "Pack:" label with it, so the row does not show a stray option name.
      const option = select.closest('.kaching-bundles__bar-variant-option');
      if (option) setHidden(option, hide);
    });
    // A variant row left without a visible select would show a stray option name.
    bundle.querySelectorAll('.kaching-bundles__bar-variant').forEach((variant) => {
      const selects = variant.querySelectorAll('.kaching-bundles__bar-variant-select');
      const visible = [...selects].some((select) => !select.hasAttribute(HIDDEN_MARK));
      const swatches = variant.querySelector(
        '.kaching-bundles__swatch-dropdown, .kaching-bundles__swatch-buttons'
      );
      setHidden(variant, selects.length > 0 && !visible && !swatches);
    });
    bundle.querySelectorAll('.kaching-bundles__bar-variants, .kaching-bundles__bar-variant-selects').forEach((group) => {
      const items = group.querySelectorAll('.kaching-bundles__bar-variant, .kaching-bundles__bar-variant-option');
      const visible = [...items].some((item) => !item.hasAttribute(HIDDEN_MARK));
      setHidden(group, items.length > 0 && !visible);
    });
  };

  /**
   * Theme toggle in place of the Kaching checkbox. The Kaching row (role="checkbox") stays the
   * single source of truth: its own click handler flips aria-checked, and the toggle just mirrors
   * that. The switch template has pointer-events none, so clicks land on the row itself.
   */
  const syncSwitch = (row) => {
    if (!switchEnabled) return;
    const main = row.querySelector('.kaching-bundles__bar-main');
    if (!main) return;
    let toggle = main.querySelector('.ek-kaching-switch');
    if (!toggle) {
      // First child of the row, in the slot the (hidden) Kaching checkbox used.
      main.insertAdjacentHTML('afterbegin', switchTemplate.innerHTML);
      toggle = main.querySelector('.ek-kaching-switch');
      if (!toggle) return;
    }
    const input = toggle.querySelector('input');
    const checked = main.getAttribute('aria-checked') === 'true';
    if (input && input.checked !== checked) input.checked = checked;
    const disabled = main.getAttribute('aria-disabled') === 'true';
    if (input && input.disabled !== disabled) input.disabled = disabled;
  };

  /* ---------------------------------------------------------------------------------------
   * Add button mode
   *
   * The row is added to the cart on its own instead of being staged for the main add to cart.
   * The Kaching checkbox is left unchecked and every click inside the row is swallowed, so the
   * same add-on can never travel with the bundle as well and be charged twice.
   * ------------------------------------------------------------------------------------- */
  const cartProductIds = new Set();
  const productCache = new Map();
  let cartLoaded = false;

  const readCart = async () => {
    try {
      const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;
      const cart = await response.json();
      cartProductIds.clear();
      (cart.items || []).forEach((item) => cartProductIds.add(String(item.product_id)));
      cartLoaded = true;
      scan();
    } catch (error) {
      cartLoaded = false;
    }
  };

  const fetchProduct = async (url) => {
    if (!url) return null;
    if (productCache.has(url)) return productCache.get(url);
    const request = fetch(`${url}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
    productCache.set(url, request);
    return request;
  };

  const addToCart = async (button) => {
    const url = button.getAttribute('data-ek-kaching-url');
    if (!url || button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    try {
      const product = await fetchProduct(url);
      const variants = product?.variants || [];
      const variant = variants.find((candidate) => candidate.available) || variants[0];
      if (!variant) throw new Error('No variant available');
      const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to add to cart');
      if (product?.id != null) cartProductIds.add(String(product.id));
      button.setAttribute('data-added', 'true');
      if (typeof window.publish === 'function') {
        window.publish('cart:added', { items: data.items, quantity: 1 });
      }
      window.toast?.success?.(window.toast_translations?.cart?.success);
    } catch (error) {
      console.error('[ek-kaching] add to cart failed', error);
      window.toast?.error?.(window.toast_translations?.cart?.error);
    } finally {
      button.removeAttribute('aria-busy');
    }
  };

  const syncAddButton = (row, product) => {
    if (!addMode) return;
    const main = row.querySelector('.kaching-bundles__bar-main');
    if (!main) return;
    let button = main.querySelector('.ek-kaching-add');
    if (!button) {
      main.insertAdjacentHTML('beforeend', addTemplate.innerHTML);
      button = main.querySelector('.ek-kaching-add');
      if (!button) return;
    }
    if (product.url && button.getAttribute('data-ek-kaching-url') !== product.url) {
      button.setAttribute('data-ek-kaching-url', product.url);
    }
    if (!cartLoaded || !product.id) return;
    const added = cartProductIds.has(product.id) ? 'true' : 'false';
    if (button.getAttribute('data-added') !== added) button.setAttribute('data-added', added);
  };

  /**
   * In add mode nothing inside an add-on row may reach Kaching's own row handler. Capturing on
   * the bundle keeps this ahead of it whether Kaching binds on the row or higher up.
   */
  const guardAddMode = (bundle) => {
    if (!addMode || bundle.hasAttribute('data-ek-kaching-add-guard')) return;
    bundle.setAttribute('data-ek-kaching-add-guard', '');
    bundle.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (!target.closest(UPSELL_SELECTOR)) return;
        if (target.closest('.ek-kaching-tooltip')) return;
        event.stopPropagation();
        event.preventDefault();
        const button = target.closest('.ek-kaching-add');
        if (button) addToCart(button);
      },
      true
    );
  };

  const enhance = (bundle) => {
    if (switchEnabled && !bundle.hasAttribute('data-ek-kaching-switch')) {
      bundle.setAttribute('data-ek-kaching-switch', '');
    }
    if (addMode && !bundle.hasAttribute('data-ek-kaching-add-mode')) {
      bundle.setAttribute('data-ek-kaching-add-mode', '');
    }
    guardAddMode(bundle);
    syncNote(bundle);
    syncFreeBadges(bundle);
    syncVariantSelects(bundle);
    const rows = bundle.querySelectorAll(UPSELL_SELECTOR);
    if (!rows.length) return;
    const products = readProducts(bundle);
    const upsellIds = readUpsellIds(bundle);
    const upsellHandles = readUpsellHandles(bundle);
    rows.forEach((row, index) => {
      if (!row.hasAttribute(ROW_MARK)) {
        row.setAttribute(ROW_MARK, '');
      }
      const product = resolveProduct(row, index, bundle, products, upsellIds, upsellHandles);
      addTooltip(row, product.url);
      syncBadge(row);
      syncSwitch(row);
      syncAddButton(row, product);
    });
  };

  const observe = (bundle) => {
    if (observed.has(bundle)) {
      enhance(bundle);
      return;
    }
    observed.add(bundle);
    let scheduled = false;
    const run = () => {
      scheduled = false;
      enhance(bundle);
    };
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(run);
    });
    observer.observe(bundle, { childList: true, subtree: true, characterData: true, attributes: true });
    enhance(bundle);
  };

  const scan = () => {
    document.querySelectorAll('kaching-bundle').forEach(observe);
  };

  scan();

  if (addMode) {
    readCart();
    if (typeof window.subscribe === 'function') {
      window.subscribe('cart:added', () => readCart());
      window.subscribe('cart:updated', () => readCart());
    }
    document.documentElement.addEventListener('cart:change', () => readCart());
  }

  // Kaching can mount later than this script, or a section can be re-rendered with a fresh element.
  const bodyObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches?.('kaching-bundle') || node.querySelector?.('kaching-bundle')) {
          scan();
          return;
        }
      }
    }
  });
  bodyObserver.observe(document.body, { childList: true, subtree: true });

  /**
   * Storefront diagnostic: window.EKSTER_KACHING_ADD_ONS.diagnose() in the console.
   */
  const diagnose = () => {
    const report = {
      config,
      tooltipTemplate: Boolean(tooltipTemplate),
      badgeTemplate: Boolean(badgeTemplate),
      freeBadgeTemplate: Boolean(freeBadgeTemplate),
      noteTemplate: Boolean(noteTemplate),
      addMode,
      loaderDefined: Boolean(customElements.get('section-dialog-loader')),
      bundles: [],
    };
    document.querySelectorAll('kaching-bundle').forEach((bundle) => {
      const products = readProducts(bundle);
      const upsellIds = readUpsellIds(bundle);
      const upsellHandles = readUpsellHandles(bundle);
      const rows = [...bundle.querySelectorAll(UPSELL_SELECTOR)].map((row, index) => ({
        alt: row.querySelector('img')?.getAttribute('alt') || '',
        pathname: resolvePathname(row, index, bundle, products, upsellIds, upsellHandles),
        firstLine: Boolean(row.querySelector('.kaching-bundles__bar-first-line')),
        tooltip: Boolean(row.querySelector('.ek-kaching-tooltip')),
        tooltipVisible: Boolean(row.querySelector('.ek-kaching-tooltip')?.getBoundingClientRect().width),
        badge: row.querySelector('.ek-kaching-badge')?.textContent || null,
        freeBadge: Boolean(row.querySelector('.ek-kaching-free-badge')),
        toggle: row.querySelector('.ek-kaching-switch input')?.checked ?? null,
        addButton: row.querySelector('.ek-kaching-add')?.getAttribute('data-added') ?? null,
      }));
      const bars = [...bundle.querySelectorAll('.kaching-bundles__bar')].map((bar) => ({
        selected: bar.classList.contains('kaching-bundles__bar--selected'),
        freeItem: barHasFreeItem(bar),
        note: Boolean(bar.querySelector('.ek-kaching-note')),
        selects: [...bar.querySelectorAll('.kaching-bundles__bar-variant-select')].map(
          (select) =>
            `${selectLabels(select).join(' / ')}${select.hasAttribute(HIDDEN_MARK) ? ' [hidden]' : ''}`
        ),
      }));
      report.bundles.push({
        products: [...products.values()].map((p) => `${p.id}:${p.handle}`),
        upsellIds,
        upsellHandles,
        prefix: readPrefix(bundle),
        bars,
        rows,
      });
    });
    console.table(report.bundles.flatMap((b) => b.rows));
    console.table(report.bundles.flatMap((b) => b.bars));
    return report;
  };

  window.EKSTER_KACHING_ADD_ONS = { refresh: scan, diagnose };
})();